import { useTheme } from '../../context/ThemeContext';
import { ApiKeyStatus, ClientStatus, HealthState, QuotaScope } from '../../gql/types';

// Semantic status pill (DESIGN_LANGUAGE_SPEC §3 "Color used semantically"). Maps each domain
// status to a color family so state is legible at a glance across the tenancy tree.

type Tone = 'active' | 'warning' | 'neutral' | 'danger';

const TONE_STYLES: Record<Tone, { dark: string; light: string }> = {
  active: {
    dark: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300',
    light: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  warning: {
    dark: 'border-amber-500/20 bg-amber-500/10 text-amber-300',
    light: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  neutral: {
    dark: 'border-white/[0.1] bg-white/[0.04] text-white/60',
    light: 'border-slate-200 bg-slate-100 text-slate-600',
  },
  danger: {
    dark: 'border-rose-500/20 bg-rose-500/10 text-rose-300',
    light: 'border-rose-200 bg-rose-50 text-rose-700',
  },
};

const CLIENT_TONE: Record<ClientStatus, Tone> = {
  [ClientStatus.Active]: 'active',
  [ClientStatus.Suspended]: 'warning',
  [ClientStatus.Archived]: 'neutral',
};

const KEY_TONE: Record<ApiKeyStatus, Tone> = {
  [ApiKeyStatus.Active]: 'active',
  [ApiKeyStatus.Revoked]: 'danger',
};

// Provider health: healthy=emerald(active), degraded=amber(warning), down=rose(danger).
const HEALTH_TONE: Record<HealthState, Tone> = {
  [HealthState.Healthy]: 'active',
  [HealthState.Degraded]: 'warning',
  [HealthState.Down]: 'danger',
};

const HEALTH_LABEL: Record<HealthState, string> = {
  [HealthState.Healthy]: 'Healthy',
  [HealthState.Degraded]: 'Degraded',
  [HealthState.Down]: 'Down',
};

const SCOPE_LABEL: Record<QuotaScope, string> = {
  [QuotaScope.Key]: 'Key',
  [QuotaScope.App]: 'App',
  [QuotaScope.Client]: 'Client',
};

export interface StatusBadgeProps {
  tone: Tone;
  children: string;
}

export function StatusBadge({ tone, children }: StatusBadgeProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const styles = TONE_STYLES[tone];
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
        isDark ? styles.dark : styles.light
      }`}
    >
      {children}
    </span>
  );
}

export function ClientStatusBadge({ status }: { status: ClientStatus }) {
  return <StatusBadge tone={CLIENT_TONE[status]}>{status}</StatusBadge>;
}

export function ApiKeyStatusBadge({ status }: { status: ApiKeyStatus }) {
  return <StatusBadge tone={KEY_TONE[status]}>{status}</StatusBadge>;
}

export function HealthStateBadge({ state }: { state: HealthState }) {
  return <StatusBadge tone={HEALTH_TONE[state]}>{HEALTH_LABEL[state]}</StatusBadge>;
}

export function QuotaScopeBadge({ scope }: { scope: QuotaScope }) {
  return <StatusBadge tone="neutral">{SCOPE_LABEL[scope]}</StatusBadge>;
}
